import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { MapPin, X, Award, Download } from "lucide-react";
import ShareTruth from "@/components/ShareTruth";

const PRIVACY = [
  { key: "city_only", label: "City Only", recommended: true, desc: "📍 City & state shown · Owner shown as Anonymous Truth Keeper" },
  { key: "public", label: "Public", desc: "📍 City & state shown · Your display name visible to all" },
  { key: "private", label: "Private", desc: "Private Collection · Location & owner hidden · Still counted in stats" },
  { key: "business", label: "Business", desc: "📍 Business name + city shown · Great for coffee shops, libraries, etc." },
];

export default function MyTruths() {
  const [truths, setTruths] = useState([]);
  const [badges, setBadges] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [t, b] = await Promise.all([http.get("/my/truths"), http.get("/my/badges").catch(() => ({ data: [] }))]);
      setTruths(t.data); setBadges(b.data || []);
    } catch { toast.error("Could not load your truths"); }
  };
  useEffect(() => { load(); }, []);

  const startReg = (t) => setEditing({
    truth_id: t.truth_id,
    privacy: t.privacy || "city_only",
    owner_display_name: t.owner_display_name || "",
    business_name: t.business_name || "",
    business_website: t.business_website || "",
    co_owner_display_name: t.co_owner_display_name || "",
    co_owner_email: t.co_owner_email || "",
    city: t.city || "",
    state: t.state || "",
    country: t.country || "US",
  });

  const save = async () => {
    try {
      await http.post(`/truths/${editing.truth_id}/register`, editing);
      toast.success(`${editing.truth_id} registered!`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed");
    }
  };

  const downloadCert = async (tid) => {
    try {
      const { data } = await http.get(`/truths/${tid}/certificate.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `${tid}-certificate.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not download certificate"); }
  };

  return (
    <div className="space-y-6" data-testid="my-truths">
      <div>
        <div className="font-display text-3xl text-[#F2EFE8]">Your Truths</div>
        <p className="font-body text-[13px] text-[#8a857d] mt-1">Every paid print has a unique Truth ID. Register yours to place it on the live Truth Map.</p>
      </div>

      {badges.length > 0 && (
        <div className="border border-[#C9A055]/20 bg-[#0a0a0a] p-5" data-testid="my-badges">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3 inline-flex items-center gap-2"><Award size={12}/> COLLECTOR BADGES</div>
          <div className="flex flex-wrap gap-3">
            {badges.map((b) => (
              <div key={b.slug} className="flex items-center gap-3 border border-[#C9A055]/40 bg-black px-4 py-2.5" data-testid={`badge-${b.slug}`}>
                <div className="w-9 h-9 rounded-full border border-[#C9A055] flex items-center justify-center text-[#C9A055]">
                  <Award size={16}/>
                </div>
                <div>
                  <div className="font-ui text-[11px] tracking-[0.22em] text-[#F2EFE8] uppercase">{b.label}</div>
                  <div className="font-body text-[10px] text-[#8a857d]">{b.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {truths.length === 0 ? (
        <div className="border border-white/10 bg-[#0a0a0a] p-10 text-center font-body text-[#8a857d] italic">
          No truths yet. Once your order is paid, your Truth IDs will appear here.
        </div>
      ) : (
        <div className="grid gap-3">
          {truths.map((t) => (
            <div key={t.truth_id} className="border border-white/10 bg-[#0a0a0a] p-5 flex items-start justify-between gap-4 flex-wrap" data-testid={`my-truth-${t.truth_id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055]">{t.truth_id}</div>
                <div className="font-display text-xl text-[#F2EFE8] mt-1 uppercase">{t.artwork_title}</div>
                <div className="font-body text-[12px] text-[#8a857d] mt-2">
                  {t.status === "registered" ? (
                    <span className="inline-flex items-center gap-1 text-[#5fc377]"><MapPin size={11}/> Registered · {t.privacy.toUpperCase()}</span>
                  ) : (
                    <span className="text-orange-300">Unregistered — register to place on the map</span>
                  )}
                </div>
                <div className="mt-2 flex gap-3 flex-wrap items-center">
                  {t.status === "registered" && (
                    <Link to={`/truths/${t.truth_id}`} className="font-ui text-[10px] text-[#8a857d] hover:text-[#C9A055] tracking-[0.22em] uppercase inline-block">View Truth Page →</Link>
                  )}
                  <button onClick={() => downloadCert(t.truth_id)} data-testid={`download-cert-${t.truth_id}`} className="font-ui text-[10px] text-[#C9A055] hover:underline tracking-[0.22em] uppercase inline-flex items-center gap-1">
                    <Download size={11}/> Download Certificate
                  </button>
                  {t.status === "registered" && (
                    <ShareTruth
                      truth_id={t.truth_id}
                      label="Share"
                      className="font-ui text-[10px] text-[#C9A055] hover:underline tracking-[0.22em] uppercase inline-flex items-center gap-1 cursor-pointer"
                    />
                  )}
                </div>
              </div>
              <button onClick={() => startReg(t)} data-testid={`register-${t.truth_id}`} className="px-4 py-2 bg-[#C9A055] text-black hover:opacity-90 font-ui text-[10px] tracking-[0.32em] uppercase whitespace-nowrap">
                {t.status === "registered" ? "Update" : "Register"}
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#050505] border border-[#C9A055]/30 max-w-xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="register-modal">
            <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center">
              <div>
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">REGISTER · {editing.truth_id}</div>
                <div className="font-display text-xl text-[#F2EFE8]">Place your truth on the map</div>
              </div>
              <button onClick={() => setEditing(null)} className="text-[#8a857d] hover:text-[#F4F1EA]"><X size={20}/></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">PRIVACY</div>
                <div className="space-y-2">
                  {PRIVACY.map((p) => (
                    <label key={p.key} className={`block border p-3 cursor-pointer ${editing.privacy === p.key ? "border-[#C9A055] bg-[#C9A055]/10" : "border-white/15 hover:border-white/30"}`}>
                      <div className="flex items-center gap-3">
                        <input type="radio" checked={editing.privacy === p.key} onChange={() => setEditing({ ...editing, privacy: p.key })} className="accent-[#C9A055]"/>
                        <div>
                          <div className="font-ui text-[12px] text-[#F4F1EA] uppercase">
                            {p.label} {p.recommended && <span className="ml-2 text-[10px] text-[#C9A055]">⭐ RECOMMENDED</span>}
                          </div>
                          <div className="font-body text-[11px] text-[#8a857d] mt-1">{p.desc}</div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {editing.privacy === "public" && (
                <Input label="Your display name" value={editing.owner_display_name} onChange={(v) => setEditing({ ...editing, owner_display_name: v })} testid="reg-display-name"/>
              )}
              {editing.privacy === "business" && (
                <>
                  <Input label="Business name" value={editing.business_name} onChange={(v) => setEditing({ ...editing, business_name: v })} testid="reg-business-name"/>
                  <Input label="Website (optional)" value={editing.business_website} onChange={(v) => setEditing({ ...editing, business_website: v })} testid="reg-business-website"/>
                </>
              )}
              {(editing.privacy === "public" || editing.privacy === "business") && (
                <div className="border border-white/10 bg-black/30 p-3 space-y-3">
                  <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">CO-OWNER (OPTIONAL)</div>
                  <p className="font-body text-[11px] text-[#8a857d]">Was this a gift? Co-credit your partner, parent, child, friend.</p>
                  <Input label="Co-owner name" value={editing.co_owner_display_name} onChange={(v) => setEditing({ ...editing, co_owner_display_name: v })} testid="reg-coowner-name"/>
                </div>
              )}
              {editing.privacy !== "private" && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input label="City" value={editing.city} onChange={(v) => setEditing({ ...editing, city: v })} testid="reg-city"/>
                  <Input label="State / Region" value={editing.state} onChange={(v) => setEditing({ ...editing, state: v })} testid="reg-state"/>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-4 py-2 border border-white/20 text-[#9a9690] font-ui text-[10px] tracking-[0.32em] uppercase">Cancel</button>
              <button onClick={save} data-testid="reg-submit" className="px-6 py-2 bg-[#C9A055] text-black hover:opacity-90 font-ui text-[10px] tracking-[0.32em] uppercase">Save Registration</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1 uppercase">{label}</div>
      <input
        className="w-full bg-black border border-white/15 px-3 py-2 text-[#F4F1EA] font-body text-[14px] focus:border-[#C9A055] focus:outline-none"
        value={value || ""} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </label>
  );
}
